import { useState, useCallback } from 'react';

interface ProcessingState {
  isProcessing: boolean;
  currentStep: number;
  progress: number;
  estimatedTime: number;
  error: string | null;
}

interface VideoResult {
  videoUrl: string;
  transcript: Array<{
    timestamp: number;
    text: string;
    speaker?: string;
  }>;
  metadata: {
    title: string;
    channel: string;
    views: string;
    publishDate: string;
    originalDuration: number;
    summaryDuration: number;
    compressionRatio: number;
  };
}

export const useVideoProcessor = () => {
  const [state, setState] = useState<ProcessingState>({
    isProcessing: false,
    currentStep: 0,
    progress: 0,
    estimatedTime: 0,
    error: null,
  });

  const [result, setResult] = useState<VideoResult | null>(null);

  const processVideo = useCallback(async (url: string) => {
    setState({
      isProcessing: true,
      currentStep: 1,
      progress: 0,
      estimatedTime: 120,
      error: null,
    });

    try {
      // Simulate processing steps
      const steps = [
        { step: 1, duration: 3000, progress: 25 },
        { step: 2, duration: 4000, progress: 50 },
        { step: 3, duration: 3000, progress: 75 },
        { step: 4, duration: 2000, progress: 100 },
      ];

      for (const stepData of steps) {
        await new Promise(resolve => setTimeout(resolve, stepData.duration));
        
        setState(prev => ({
          ...prev,
          currentStep: stepData.step,
          progress: stepData.progress,
          estimatedTime: Math.max(0, prev.estimatedTime - stepData.duration / 1000),
        }));
      }

      // Mock successful result
      const mockResult: VideoResult = {
        videoUrl: url,
        transcript: [
          {
            timestamp: 0,
            text: "Welcome to this comprehensive guide on artificial intelligence and machine learning. In today's rapidly evolving technological landscape, understanding AI has become crucial for businesses and individuals alike.",
            speaker: "Narrator"
          },
          {
            timestamp: 15,
            text: "We'll explore the fundamental concepts, practical applications, and future implications of AI technology. From neural networks to deep learning algorithms, we'll break down complex topics into digestible insights.",
            speaker: "Narrator"
          },
          {
            timestamp: 35,
            text: "Machine learning represents a subset of AI that enables systems to automatically learn and improve from experience without being explicitly programmed. This revolutionary approach has transformed industries from healthcare to finance.",
            speaker: "Narrator"
          },
          {
            timestamp: 55,
            text: "As we dive deeper into this subject, you'll discover how AI is reshaping our world and what skills you need to stay relevant in an AI-driven future. Let's begin our journey into the fascinating world of artificial intelligence.",
            speaker: "Narrator"
          }
        ],
        metadata: {
          title: "The Complete Guide to AI and Machine Learning in 2024",
          channel: "TechEducation Pro",
          views: "1.2M",
          publishDate: "2 weeks ago",
          originalDuration: 1800, // 30 minutes
          summaryDuration: 300,   // 5 minutes
          compressionRatio: 0.83, // 83% compression
        }
      };

      setResult(mockResult);
      setState(prev => ({ ...prev, isProcessing: false }));

    } catch (error) {
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: 'Failed to process video. Please check the URL and try again.',
      }));
    }
  }, []);

  const cancel = useCallback(() => {
    setState({
      isProcessing: false,
      currentStep: 0,
      progress: 0,
      estimatedTime: 0,
      error: null,
    });
    setResult(null);
  }, []);

  const retry = useCallback(() => {
    setState(prev => ({
      ...prev,
      error: null,
    }));
  }, []);

  return {
    state,
    result,
    processVideo,
    cancel,
    retry,
  };
};